/**
 * Barre di riempimento per quote e dischi.
 *
 * Una barra dice a colpo d'occhio ciò che un numero non dice: 340 GB non
 * significa niente da solo, «l'87% del volume» significa che fra poco si smette
 * di raccogliere. È il motivo per cui esiste questo componente e non si stampa il
 * numero e basta.
 *
 * Le due regole che segue, e che sono la ragione per cui non è un `<progress>`:
 *
 *  - **Senza limite non c'è barra.** Un piano illimitato o un container senza
 *    tetto di memoria non hanno una percentuale: disegnare una barra vuota
 *    direbbe «pieno allo 0%», cioè che c'è moltissimo spazio, che è una risposta
 *    a una domanda che non è stata posta. Al suo posto compare il valore e basta.
 *
 *  - **Il colore cambia dove cambia la decisione.** Non è gradiente: sotto il 75%
 *    non si fa niente, dal 75% si pianifica, dal 90% si interviene adesso. Tre
 *    stati perché sono tre azioni.
 */

import { formatBytes } from './charts'
import { localeCorrente, t } from '../i18n/lingua'

/** Soglie in cui cambia ciò che conviene fare, non ciò che è bello vedere. */
export function tone(percent: number | null | undefined): 'ok' | 'warn' | 'error' {
  if (percent == null) return 'ok'
  if (percent >= 90) return 'error'
  if (percent >= 75) return 'warn'
  return 'ok'
}

export function Meter({
  label,
  used,
  limit,
  percent,
  format = (n: number) => n.toLocaleString(localeCorrente()),
  unlimitedNote,
}: {
  label: string
  used: number | null
  limit: number | null
  percent: number | null
  format?: (n: number) => string
  /** Perché non c'è un limite. Senza, «illimitato» e «non misurabile» si confondono. */
  unlimitedNote?: string
}) {
  const value = used == null ? '—' : format(used)

  return (
    <div className="meter">
      <div className="meter__head">
        <span className="meter__label">{label}</span>
        <span className="meter__value">
          {value}
          {limit != null && <span className="meter__limit"> / {format(limit)}</span>}
        </span>
      </div>

      {percent != null && limit != null ? (
        <>
          <div className="meter__track">
            <div
              className={`meter__fill meter__fill--${tone(percent)}`}
              // Oltre il 100% la barra si ferma al bordo ma il numero no: un
              // tenant che ha superato la quota **deve** vedere di quanto, e una
              // barra che sfonda il contenitore romperebbe il layout senza dirlo
              // meglio.
              style={{ width: `${Math.min(100, percent)}%` }}
            />
          </div>
          <div className="meter__foot">
            <span className={percent >= 90 ? 'badge--warn' : undefined}>{percent}%</span>
          </div>
        </>
      ) : (
        <div className="meter__foot meter__foot--muted">
          {unlimitedNote ?? t('nessun limite impostato')}
        </div>
      )}
    </div>
  )
}

/** Un disco: usato, libero, totale. */
export function DiskMeter({
  label,
  used,
  total,
  percent,
  note,
}: {
  label: string
  used: number
  total: number
  percent: number | null
  note?: string
}) {
  return (
    <div className="meter">
      <div className="meter__head">
        <span className="meter__label">{label}</span>
        <span className="meter__value">
          {formatBytes(used)}
          <span className="meter__limit"> / {formatBytes(total)}</span>
        </span>
      </div>
      <div className="meter__track">
        <div
          className={`meter__fill meter__fill--${tone(percent)}`}
          style={{ width: `${Math.min(100, percent ?? 0)}%` }}
        />
      </div>
      <div className="meter__foot">
        <span className={tone(percent) !== 'ok' ? 'badge--warn' : undefined}>
          {percent == null ? '—' : `${percent}%`}
        </span>
        {/* Lo spazio **libero** e non solo la percentuale: è il numero con cui si
            decide, perché «12% libero» su 200 GB e su 200 TB sono due situazioni
            che non si somigliano. */}
        <span className="meter__free">{t('{spazio} liberi', { spazio: formatBytes(total - used) })}</span>
        {note && <span className="meter__note">{note}</span>}
      </div>
    </div>
  )
}
